import { useCallback, useEffect, useRef, useState } from 'react';

export const MIN_WIDTH_ZOOM = 1;
export const MAX_WIDTH_ZOOM = 8;
export const WIDTH_ZOOM_STEP = 0.25;
/** Wheel zoom sensitivity (pixels of deltaY → zoom units). Lower = slower. */
export const WHEEL_ZOOM_FACTOR = 0.00085;
/** Per-frame lerp toward target zoom (0–1). Lower = smoother / slower. */
export const ZOOM_LERP = 0.08;
const ZOOM_SETTLE_EPSILON = 0.004;

export function clampWidthZoom(next: number): number {
  const rounded = Math.round(next * 100) / 100;
  return Math.min(MAX_WIDTH_ZOOM, Math.max(MIN_WIDTH_ZOOM, rounded));
}

export function snapZoomStep(next: number): number {
  const snapped = Math.round(next / WIDTH_ZOOM_STEP) * WIDTH_ZOOM_STEP;
  return clampWidthZoom(snapped);
}

interface UseChartWidthZoomOptions {
  enabled?: boolean;
}

export function useChartWidthZoom({ enabled = true }: UseChartWidthZoomOptions = {}) {
  const [targetZoom, setTargetZoom] = useState(MIN_WIDTH_ZOOM);
  const [displayZoom, setDisplayZoom] = useState(MIN_WIDTH_ZOOM);
  const [viewportWidth, setViewportWidth] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollCenterRatioRef = useRef(0.5);
  const targetZoomRef = useRef(MIN_WIDTH_ZOOM);
  const displayZoomRef = useRef(MIN_WIDTH_ZOOM);
  const rafRef = useRef<number | null>(null);

  const captureScrollCenter = useCallback(() => {
    const el = scrollRef.current;
    if (el && el.scrollWidth > 0) {
      scrollCenterRatioRef.current =
        (el.scrollLeft + el.clientWidth / 2) / el.scrollWidth;
    }
  }, []);

  const applyScrollCenter = useCallback((zoom: number) => {
    const el = scrollRef.current;
    if (!el) return;
    if (zoom <= MIN_WIDTH_ZOOM) {
      el.scrollLeft = 0;
      return;
    }
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 0) {
      el.scrollLeft = 0;
      return;
    }
    el.scrollLeft = scrollCenterRatioRef.current * el.scrollWidth - el.clientWidth / 2;
  }, []);

  const startZoomAnimation = useCallback(() => {
    if (rafRef.current != null) return;

    const tick = () => {
      const current = displayZoomRef.current;
      const target = targetZoomRef.current;
      const delta = target - current;
      if (Math.abs(delta) < ZOOM_SETTLE_EPSILON) {
        displayZoomRef.current = target;
        setDisplayZoom(target);
        applyScrollCenter(target);
        rafRef.current = null;
        return;
      }
      const next = current + delta * ZOOM_LERP;
      displayZoomRef.current = next;
      setDisplayZoom(next);
      applyScrollCenter(next);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
  }, [applyScrollCenter]);

  const applyZoom = useCallback(
    (nextZoom: number) => {
      captureScrollCenter();
      const clamped = clampWidthZoom(nextZoom);
      targetZoomRef.current = clamped;
      setTargetZoom(clamped);
      startZoomAnimation();
    },
    [captureScrollCenter, startZoomAnimation],
  );

  useEffect(() => {
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !enabled) return;
    const updateWidth = () => setViewportWidth(el.clientWidth);
    updateWidth();
    const ro = new ResizeObserver(updateWidth);
    ro.observe(el);
    return () => ro.disconnect();
  }, [enabled]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !enabled) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      captureScrollCenter();
      const cappedDelta = Math.max(-80, Math.min(80, e.deltaY));
      const next = clampWidthZoom(targetZoomRef.current - cappedDelta * WHEEL_ZOOM_FACTOR);
      targetZoomRef.current = next;
      setTargetZoom(next);
      startZoomAnimation();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [enabled, captureScrollCenter, startZoomAnimation]);

  const chartPixelWidth =
    viewportWidth > 0 ? Math.max(viewportWidth, viewportWidth * displayZoom) : undefined;

  return {
    scrollRef,
    targetZoom,
    displayZoom,
    chartPixelWidth,
    applyZoom,
    zoomIn: () => applyZoom(snapZoomStep(targetZoomRef.current + WIDTH_ZOOM_STEP)),
    zoomOut: () => applyZoom(snapZoomStep(targetZoomRef.current - WIDTH_ZOOM_STEP)),
    resetZoom: () => applyZoom(MIN_WIDTH_ZOOM),
  };
}
